import React, { useState, useEffect } from 'react';

import { Link, useParams, useNavigate, useSearchParams} from 'react-router-dom';
import {
  Container, Box, Typography, Tabs, Tab,
  useTheme, useMediaQuery
} from '@mui/material';

import DateNavigator from '../components/DateNavigator';
import { COLOR_MAP } from '../constants';

import DeviceCharts from "../components/DeviceCharts";
import DeviceRecords from "../components/DeviceRecords";
import CustomMap from "../components/CustomMap";

import { TimelineOutlined, ListOutlined, AutoAwesomeMosaicOutlined, PlaceOutlined } from '@mui/icons-material';
import DeviceControl from '../components/DeviceControlPanel';

export default function DeviceDetailsPage() {

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { id, tab } = useParams();
  const navigate = useNavigate();

  // Tabs
  const tabMapping = ['overview', 'records', 'locations', 'control-panel'];

  const currentTabValue = tabMapping.indexOf(tab) !== -1 ? tabMapping.indexOf(tab) : 0;

  const handleTabChange = (event, newValue) => {
    const targetTab = tabMapping[newValue];
    navigate(`/devices/${id}/${targetTab}`);
  };

  // Data
  const [settings, setSettingsState] = useState(() => {
    const saved = sessionStorage.getItem(`device_settings_${id}`);
    return saved ? JSON.parse(saved) : { name: null, time_1: null, time_2: null, time_3: null };
  });

  const [tempSettings, setTempSettings] = useState(settings);

  const [saving, setSaving] = useState(false);

  const [info, setInfoState] = useState(() => {
    const saved = sessionStorage.getItem(`device_info_${id}`);
    return saved ? JSON.parse(saved) : { id: null, active: null, model: null, gps: null, date_synchronized: null, date_manufactured: null };
  });

  const [balance, setBalanceState] = useState(() => {
    const saved = sessionStorage.getItem(`device_balance_${id}`);
    try {
      return saved ? JSON.parse(saved) : { amount: null, history: null };
    } catch (err) {
      return { amount: null, history: null };
    }
  });

  const setSettings = (newSettings) => {
    setSettingsState(newSettings);
    setTempSettings(newSettings);
    sessionStorage.setItem(`device_settings_${id}`, JSON.stringify(newSettings));
  };

  const setInfo = (newInfo) => {
    setInfoState(newInfo);
    sessionStorage.setItem(`device_info_${id}`, JSON.stringify(newInfo));
  };

  const setBalance = (newBalance) => {
    setBalanceState(newBalance);
    sessionStorage.setItem(`device_balance_${id}`, JSON.stringify(newBalance));
  };

  useEffect(() => {
    const saved = sessionStorage.getItem(`device_settings_${id}`) && sessionStorage.getItem(`device_info_${id}`) && sessionStorage.getItem(`device_balance_${id}`);
    if (!saved) {
      const fetchData = async () => {
        try {
          const token = localStorage.getItem('auth_token');
          const response = await fetch(`${process.env.REACT_APP_API_URL}/user/device/${id}/data`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });

          if (response.status === 401) {
            localStorage.removeItem('auth_token');
            navigate('/login');
            return;
          }

          const allData = await response.json();
          const settingsData = allData.settings;
          setSettings(settingsData);

          const infoData = allData.info;
          setInfo(infoData);
          const balanceData = allData.balance;
          setBalance(balanceData);
        } catch (err) {
          console.error("Data fetch error:", err);
        }
      };
      fetchData();
    }
  }, [id]);

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/user/device/${id}/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(tempSettings)
      });

      if (response.ok) {
        setSettings(tempSettings);
      }
    } finally {
      setSaving(false);
    }
  };

  // Records
  const [records, setRecords] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);

  const [filters, setFiltersState] = useState(() => {
    const saved = sessionStorage.getItem(`device_filters_${id}`);
    if (saved) {
      return JSON.parse(saved);
    } else {
      const today = new Date();
      return {
        mode: 'day',
        year: today.getFullYear(),
        month: today.getMonth() + 1,
        day: today.getDate()
      };
    }
  });

  const setFilters = (newFilters) => {
    setFiltersState(newFilters);
    sessionStorage.setItem(`device_filters_${id}`, JSON.stringify(newFilters));
  };

  useEffect(() => {
    const saved = sessionStorage.getItem(`device_filters_${id}`);
    if (saved) {
      setFiltersState(JSON.parse(saved));
    }
  }, [id]);


  useEffect(() => {

    if (currentTabValue > 1 ) return;

    const fetchRecords = async () => {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      const queryParams = new URLSearchParams(filters).toString();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/user/device/${id}/records?${queryParams}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.status === 401) {
        localStorage.removeItem('auth_token');
        navigate('/login');
        return;
      }

      const newRecords = await response.json();
      setRecords(newRecords);
      setLoading(false);
    };

    fetchRecords();
  }, [id, filters, currentTabValue]);

  useEffect(() => {

    if (currentTabValue != 2 ) return;

    const fetchLocations = async () => {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      const queryParams = new URLSearchParams(filters).toString();
      const response = await fetch(`${process.env.REACT_APP_API_URL}/user/device/${id}/locations?${queryParams}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.status === 401) {
        localStorage.removeItem('auth_token');
        navigate('/login');
        return;
      }

      const newLocations = await response.json();
      setLocations(newLocations);
      setLoading(false);
    };

    fetchLocations();
  }, [id, filters, currentTabValue]);

  const prepareChartData = () => {
    if (filters.mode === 'day') {
      return Array.from({ length: 24 }, (_, i) => {
        const hour = i < 10 ? `0${i}:00` : `${i}:00`;
        const existingEntry = records.find(d => d.time == i);
        console.log("Matching hour:", hour, "Found record:", existingEntry);
        return {
            ...existingEntry,
            time: hour
        } || { time: hour };
      });
    }

    if (filters.mode === 'month') {
      const daysInMonth = new Date(filters.year, filters.month, 0).getDate();
      return Array.from({ length: daysInMonth }, (_, i) => {
        const existingEntry = records.find(d => d.time == i + 1);
        const day = i + 1;
        return {
            ...existingEntry,
            time: day
        } || { time: day };
      });
    }

    if (filters.mode === 'year') {
      const monthLabels = ["ΙΑΝ", "ΦΕΒ", "ΜΑΡ", "ΑΠΡ", "ΜΑΪ", "ΙΟΥΝ", "ΙΟΥΛ", "ΑΥΓ", "ΣΕΠ", "ΟΚΤ", "ΝΟΕ", "ΔΕΚ"];
      return monthLabels.map((monthName, index) => {
        const monthNumberFromApi = (index + 1);
        const existingEntry = records.find(d => d.time == monthNumberFromApi);
        if (existingEntry) {
          return {
            ...existingEntry,
            time: monthName
          };
        }
        return {
          time: monthName
        };
      });
    }

    return records;
  };

  const chartData = prepareChartData();


  return (
    <Box sx={{ flexGrow: 1 }}>
      <Box sx={{
        bgcolor: 'greyCustom.soft',
        //borderBottom: `1px solid ${COLOR_MAP[settings.color] || COLOR_MAP[DEFAULT_COLOR]}`,
        pt: 4,
        pb: 0,
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        textAlign: 'center' }}>
        <Container maxWidth='xl' sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 2}}>

          <Box sx={{ flexGrow: 1 }}>
            <Typography
              variant="h5"
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1.5,
                textAlign: isMobile ? "left" : "center"
              }}
            >

              <Box
                component="span"
                sx={{
                  width: 16,
                  height: 16,
                  borderRadius: '50%',
                  bgcolor: COLOR_MAP['green'],
                  display: isMobile ? "none" : "inline-block",
                  boxShadow: '0 0 4px rgba(0,0,0,0.2)'
                }}
              />
              {settings.name || 'Χωρίς όνομα'}
            </Typography>
          </Box>
        </Container>
        <Container maxWidth='xl'  >
          <Tabs
            value={currentTabValue}
            onChange={handleTabChange}
            textColor="inherit"
            sx={{
              '& .MuiTabs-flexContainer': {
                width: '100%',
              },
              '& .MuiTabs-indicator': {
                backgroundColor: 'primary.main',
                height: '3px',
              }
            }}
          >

            <Tab
              icon={<TimelineOutlined/>}
              iconPosition="start"
              label={isMobile ? "" : "Επισκόπηση"}
              sx={{ minHeight: 48 }}
            />
            <Tab
              icon={<ListOutlined />}
              iconPosition="start"
              label={isMobile ? "" : "Καταγραφές"}
              sx={{ minHeight: 48 }}
            />
            <Tab
              icon={<PlaceOutlined />}
              iconPosition="start"
              label={isMobile ? "" : "Τοποθεσίες"}
              sx={{ minHeight: 48 }}
            />
            <Tab
              icon={<AutoAwesomeMosaicOutlined />}

              iconPosition="start"
              label={isMobile ? "" : "Διαχείριση Συσκευής"}
              sx={{ ml: 'auto', minHeight: 48 }}
            />
          </Tabs>
        </Container>
      </Box>


        {currentTabValue === 0 && (
          <Container maxWidth='xl' sx={{ mt: 4 }}>
            <Box sx={{
              opacity: loading ? 0.5 : 1,
              transition: 'opacity 0.2s ease-in-out',
              pointerEvents: loading ? 'none' : 'auto'
            }}>
              <DateNavigator filters={filters} setFilters={setFilters} />
              <DeviceCharts data={chartData} ></DeviceCharts>
            </Box>
          </Container>
        )}

        {currentTabValue === 1 && (
          <Container maxWidth='xl' sx={{ mt: 4 }}>
            <Box sx={{
              opacity: loading ? 0.5 : 1,
              transition: 'opacity 0.2s ease-in-out',
              pointerEvents: loading ? 'none' : 'auto'
            }}>
              <DateNavigator filters={filters} setFilters={setFilters} />
              <DeviceRecords data={chartData} filters={filters} ></DeviceRecords>
            </Box>
          </Container>
        )}

        {currentTabValue === 2 && (
            <Box sx={{
              opacity: loading ? 0.5 : 1,
              transition: 'opacity 0.2s ease-in-out',
              pointerEvents: loading ? 'none' : 'auto',
              position: 'relative'
            }}>
              <Box sx={{
                position: 'absolute',
                top: 16,
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 1000,
                width: 'auto',
              }}>
                <DateNavigator filters={filters} setFilters={setFilters} />
              </Box>
              <CustomMap points={locations} ></CustomMap>
            </Box>
        )}

        {currentTabValue === 3 && (
          <Container maxWidth='xl' sx={{ mt: 4 }}>
            <Box sx={{
              opacity: loading ? 0.5 : 1,
              transition: 'opacity 0.2s ease-in-out',
              pointerEvents: loading ? 'none' : 'auto'
            }}>
              <DeviceControl  info={info} balance={balance} tempSettings={tempSettings} handleSaveSettings={handleSaveSettings} setTempSettings={setTempSettings} saving={saving} ></DeviceControl>
            </Box>

          </Container>
        )}

    </Box>
  );
}
